import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/format";
import { Pencil, Plus, Trash2, RefreshCcw, Layers, BadgePercent } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${d}-${m}-${y}`;
};

const emptyActie = () => ({
  naam: "",
  start_datum: "",
  eind_datum: "",
  inkoopprijs: "",
  verkoopprijs_incl_btw: "",
});

export default function ProductPrices() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/settings/producten").then(({ data }) => setItems(data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const allYears = Array.from(
    new Set(items.flatMap((p) => Object.keys(p.inkoopprijzen_per_jaar || {})).map(Number))
  ).filter(Boolean).sort();

  const productenMetActie = items.filter((p) => (p.actieprijzen || []).length > 0);

  const handleSave = async () => {
    try {
      const { data } = await api.post("/settings/producten", editing);
      const n = data?.orders_recalculated ?? 0;
      toast.success(n > 0
        ? `Product opgeslagen · ${n} orders automatisch herberekend`
        : "Product opgeslagen");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Opslaan mislukt");
    }
  };

  const handleDelete = async (productnummer) => {
    if (!window.confirm("Product verwijderen?")) return;
    await api.delete(`/settings/producten/${encodeURIComponent(productnummer)}`);
    load();
  };

  const handleRecalc = async () => {
    if (!window.confirm("Alle marges opnieuw berekenen op basis van huidige instellingen?")) return;
    const { data } = await api.post("/settings/recalc");
    toast.success(`Herberekend: ${data.orders_updated} orders`);
  };

  const handleWebshipBulk = async () => {
    if (!window.confirm('Alle producten met productnaam beginnend met "2 x Lifjalla Water" krijgen een vaste marge van €3 per orderregel. Doorgaan?')) return;
    const { data } = await api.post("/settings/producten/webship-bulk", { naam_prefix: "2 x Lifjalla Water", vaste_marge_per_orderregel: 3.0 });
    toast.success(`${data.products_updated} Webship-producten gemarkeerd. Marges worden herberekend…`);
    const recalc = await api.post("/settings/recalc");
    toast.success(`Herberekend: ${recalc.data.orders_updated} orders`);
    load();
  };

  const [duplicaten, setDuplicaten] = useState(null);
  const openDuplicaten = async () => {
    const { data } = await api.get("/settings/producten/duplicaten");
    setDuplicaten(data.groups || []);
  };
  const autoMerge = async () => {
    if (!window.confirm("Alle gedetecteerde duplicaten samenvoegen en marges herberekenen?")) return;
    const { data } = await api.post("/settings/producten/auto-merge");
    toast.success(`${data.groups_processed} groep(en) samengevoegd · ${data.orderregels_moved} orderregels verplaatst`);
    const recalc = await api.post("/settings/recalc");
    toast.success(`Herberekend: ${recalc.data.orders_updated} orders`);
    setDuplicaten(null);
    load();
  };

  return (
    <div className="space-y-6" data-testid="product-prices-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">Catalogus</p>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 mt-1">Producten &amp; inkoopprijzen</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            Inkoopprijzen worden per jaar bewaard. Producten met vaste marge (Webship) gebruiken die in plaats van een inkoopprijs.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openDuplicaten} data-testid="consolidate-btn">
            <Layers className="h-4 w-4 mr-1.5" />
            Consolideer duplicaten
          </Button>
          <Button variant="outline" onClick={handleWebshipBulk} data-testid="webship-bulk-btn">
            Webship-marge toepassen
          </Button>
          <Button variant="outline" onClick={handleRecalc} data-testid="recalc-margins-btn">
            <RefreshCcw className="h-4 w-4 mr-1.5" />
            Herbereken marges
          </Button>
          <Button
            onClick={() => setEditing({ productnummer: "", productnaam: "", inkoopprijzen_per_jaar: {}, actief: true, opmerkingen: "", vaste_marge_per_orderregel: null, actieprijzen: [] })}
            data-testid="add-product-btn"
            className="bg-slate-900 hover:bg-slate-800"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Nieuw product
          </Button>
        </div>
      </header>

      {productenMetActie.length > 0 && (
        <Card className="bg-amber-50/60 border-amber-200 rounded-lg p-5" data-testid="actieprijzen-section">
          <div className="flex items-center gap-2 mb-1">
            <BadgePercent className="h-4 w-4 text-amber-600" />
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-amber-700">Actieprijzen — tijdelijke periodes</p>
          </div>
          <p className="text-xs text-amber-700/80 mb-4">
            Voor orders binnen deze periodes wordt automatisch de actie-inkoopprijs gebruikt bij de margeberekening, in plaats van de normale jaarprijs.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="actieprijzen-table">
              <thead>
                <tr className="border-b border-amber-200 text-amber-800">
                  <th className="text-left py-2 pr-4 font-semibold">Product</th>
                  <th className="text-left py-2 pr-4 font-semibold">Actie</th>
                  <th className="text-left py-2 pr-4 font-semibold">Periode</th>
                  <th className="text-right py-2 pr-4 font-semibold">Actie-inkoopprijs</th>
                  <th className="text-right py-2 pr-4 font-semibold">Normale inkoopprijs</th>
                  <th className="text-right py-2 font-semibold">Verkoopprijs incl. btw (ref.)</th>
                </tr>
              </thead>
              <tbody>
                {productenMetActie.flatMap((p) =>
                  (p.actieprijzen || []).map((a) => {
                    const jaar = String(a.start_datum || "").slice(0, 4);
                    const normaal = p.inkoopprijzen_per_jaar?.[jaar] ?? p.inkoopprijzen_per_jaar?.[Number(jaar)];
                    return (
                      <tr key={`${p.productnummer}-${a.id}`} className="border-b border-amber-100 last:border-0">
                        <td className="py-2 pr-4 text-slate-800">{p.productnaam}</td>
                        <td className="py-2 pr-4 text-slate-600">{a.naam || "—"}</td>
                        <td className="py-2 pr-4 tabular-nums text-slate-700 whitespace-nowrap">{fmtDate(a.start_datum)} t/m {fmtDate(a.eind_datum)}</td>
                        <td className="py-2 pr-4 text-right tabular-nums font-semibold text-amber-800">{formatCurrency(a.inkoopprijs)}</td>
                        <td className="py-2 pr-4 text-right tabular-nums text-slate-500">{normaal ? formatCurrency(normaal) : "—"}</td>
                        <td className="py-2 text-right tabular-nums text-slate-500">{a.verkoopprijs_incl_btw ? formatCurrency(a.verkoopprijs_incl_btw) : "—"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card className="bg-white border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="products-prices-table">
            <thead>
              <tr className="bg-slate-50 border-y border-slate-200 text-slate-600">
                <th className="text-left py-3 px-4 font-semibold">Productnr</th>
                <th className="text-left py-3 px-4 font-semibold">Productnaam</th>
                {allYears.map((y) => (
                  <th key={y} className="text-right py-3 px-4 font-semibold">Inkoop {y}</th>
                ))}
                <th className="text-center py-3 px-4 font-semibold">Actief</th>
                <th className="text-right py-3 px-4 font-semibold">Acties</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="6" className="text-center text-slate-400 py-8">Laden…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan="6" className="text-center text-slate-400 py-8">Geen producten. Importeer eerst een CSV of voeg handmatig toe.</td></tr>
              )}
              {items.map((p) => (
                <tr key={p.productnummer} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="py-2.5 px-4 font-medium text-slate-900">{p.productnummer}</td>
                  <td className="py-2.5 px-4 text-slate-700">
                    <div className="flex items-center gap-2">
                      <span>{p.productnaam}</span>
                      {p.vaste_marge_per_orderregel != null && (
                        <span className="inline-flex items-center text-[10px] font-semibold tracking-wider uppercase bg-sky-50 text-sky-700 border border-sky-200 px-2 py-0.5 rounded">
                          Webship · {formatCurrency(p.vaste_marge_per_orderregel)}/regel
                        </span>
                      )}
                      {(p.actieprijzen || []).length > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wider uppercase bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded" data-testid={`actie-badge-${p.productnummer}`}>
                          <BadgePercent className="h-3 w-3" />
                          Actie
                        </span>
                      )}
                    </div>
                  </td>
                  {allYears.map((y) => {
                    const v = p.inkoopprijzen_per_jaar?.[y] ?? p.inkoopprijzen_per_jaar?.[String(y)];
                    return (
                      <td key={y} className="py-2.5 px-4 text-right tabular-nums">
                        {p.vaste_marge_per_orderregel != null ? (
                          <span className="text-slate-400 text-xs">n.v.t.</span>
                        ) : v ? formatCurrency(v) : <span className="text-amber-600">—</span>}
                      </td>
                    );
                  })}
                  <td className="py-2.5 px-4 text-center">
                    {p.actief ? <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500" /> : <span className="inline-flex h-2 w-2 rounded-full bg-slate-300" />}
                  </td>
                  <td className="py-2.5 px-4 text-right">
                    <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setEditing({ ...p })} data-testid={`edit-product-${p.productnummer}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-rose-600" onClick={() => handleDelete(p.productnummer)} data-testid={`delete-product-${p.productnummer}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.productnummer ? "Product bewerken" : "Nieuw product"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label>Productnummer / SKU</Label>
                <Input
                  value={editing.productnummer}
                  onChange={(e) => setEditing({ ...editing, productnummer: e.target.value })}
                  data-testid="product-form-nummer"
                />
              </div>
              <div>
                <Label>Productnaam</Label>
                <Input
                  value={editing.productnaam}
                  onChange={(e) => setEditing({ ...editing, productnaam: e.target.value })}
                  data-testid="product-form-naam"
                />
              </div>
              <div>
                <Label>Inkoopprijzen per jaar (excl. btw)</Label>
                <div className="space-y-2 mt-2">
                  {Object.entries(editing.inkoopprijzen_per_jaar || {}).map(([y, v]) => (
                    <div key={y} className="flex gap-2 items-center">
                      <Input
                        value={y}
                        className="w-24"
                        onChange={(e) => {
                          const nm = { ...editing.inkoopprijzen_per_jaar };
                          delete nm[y];
                          nm[e.target.value] = v;
                          setEditing({ ...editing, inkoopprijzen_per_jaar: nm });
                        }}
                      />
                      <Input
                        type="number"
                        step="0.01"
                        value={v}
                        onChange={(e) => setEditing({
                          ...editing,
                          inkoopprijzen_per_jaar: { ...editing.inkoopprijzen_per_jaar, [y]: parseFloat(e.target.value) || 0 },
                        })}
                      />
                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => {
                        const nm = { ...editing.inkoopprijzen_per_jaar };
                        delete nm[y];
                        setEditing({ ...editing, inkoopprijzen_per_jaar: nm });
                      }}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const newYear = String(new Date().getFullYear());
                      setEditing({
                        ...editing,
                        inkoopprijzen_per_jaar: { ...editing.inkoopprijzen_per_jaar, [newYear]: 0 },
                      });
                    }}
                    data-testid="add-year-btn"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Jaar toevoegen
                  </Button>
                </div>
              </div>
              <div>
                <Label>Vaste marge per orderregel (€) — Webship</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Laat leeg om inkoopprijs te gebruiken"
                  value={editing.vaste_marge_per_orderregel ?? ""}
                  onChange={(e) => setEditing({
                    ...editing,
                    vaste_marge_per_orderregel: e.target.value === "" ? null : parseFloat(e.target.value),
                  })}
                  data-testid="product-form-vaste-marge"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Wanneer ingevuld: brutomarge is dit bedrag per orderregel, ongeacht aantal of korting. Inkoopprijzen per jaar worden dan genegeerd.
                </p>
              </div>
              <div>
                <Label>Actieprijzen — tijdelijke periodes</Label>
                <p className="text-xs text-slate-500 mt-0.5 mb-2">
                  Binnen de periode geldt deze inkoopprijs i.p.v. de jaarprijs. Verkoopprijs is enkel ter referentie (komt automatisch uit de orders).
                </p>
                <div className="space-y-3">
                  {(editing.actieprijzen || []).map((a, idx) => (
                    <div key={a.id || idx} className="border border-amber-200 bg-amber-50/40 rounded-md p-3 space-y-2" data-testid={`actie-row-${idx}`}>
                      <div className="flex gap-2 items-center">
                        <Input
                          placeholder="Naam actie (bv. Zomeractie)"
                          value={a.naam}
                          onChange={(e) => {
                            const list = [...editing.actieprijzen];
                            list[idx] = { ...a, naam: e.target.value };
                            setEditing({ ...editing, actieprijzen: list });
                          }}
                          data-testid={`actie-naam-${idx}`}
                        />
                        <Button size="sm" variant="ghost" className="text-rose-600 shrink-0" onClick={() => {
                          setEditing({ ...editing, actieprijzen: editing.actieprijzen.filter((_, i) => i !== idx) });
                        }} data-testid={`actie-delete-${idx}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-slate-500">Van</Label>
                          <Input
                            type="date"
                            value={a.start_datum}
                            onChange={(e) => {
                              const list = [...editing.actieprijzen];
                              list[idx] = { ...a, start_datum: e.target.value };
                              setEditing({ ...editing, actieprijzen: list });
                            }}
                            data-testid={`actie-start-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Tot en met</Label>
                          <Input
                            type="date"
                            value={a.eind_datum}
                            onChange={(e) => {
                              const list = [...editing.actieprijzen];
                              list[idx] = { ...a, eind_datum: e.target.value };
                              setEditing({ ...editing, actieprijzen: list });
                            }}
                            data-testid={`actie-eind-${idx}`}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-slate-500">Actie-inkoopprijs (excl. btw)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={a.inkoopprijs}
                            onChange={(e) => {
                              const list = [...editing.actieprijzen];
                              list[idx] = { ...a, inkoopprijs: e.target.value };
                              setEditing({ ...editing, actieprijzen: list });
                            }}
                            data-testid={`actie-inkoop-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Verkoopprijs incl. btw (ref.)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Optioneel"
                            value={a.verkoopprijs_incl_btw ?? ""}
                            onChange={(e) => {
                              const list = [...editing.actieprijzen];
                              list[idx] = { ...a, verkoopprijs_incl_btw: e.target.value };
                              setEditing({ ...editing, actieprijzen: list });
                            }}
                            data-testid={`actie-verkoop-${idx}`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    onClick={() => setEditing({ ...editing, actieprijzen: [...(editing.actieprijzen || []), emptyActie()] })}
                    data-testid="add-actie-btn"
                  >
                    <BadgePercent className="h-3.5 w-3.5 mr-1" /> Actieperiode toevoegen
                  </Button>
                </div>
              </div>
              <div>
                <Label>Opmerkingen</Label>
                <Input
                  value={editing.opmerkingen || ""}
                  onChange={(e) => setEditing({ ...editing, opmerkingen: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={editing.actief}
                  onCheckedChange={(v) => setEditing({ ...editing, actief: v })}
                  data-testid="product-form-actief"
                />
                <Label>Actief</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annuleren</Button>
            <Button onClick={handleSave} className="bg-slate-900 hover:bg-slate-800" data-testid="product-form-save">Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={duplicaten !== null} onOpenChange={(open) => !open && setDuplicaten(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Duplicaten detecteren</DialogTitle>
          </DialogHeader>
          {duplicaten?.length === 0 && (
            <p className="text-sm text-emerald-700 py-6 text-center font-medium">
              Geen duplicaten gevonden. Alle producten staan ééns in de tabel. ✨
            </p>
          )}
          {duplicaten?.length > 0 && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto" data-testid="duplicates-list">
              <p className="text-sm text-slate-600">
                {duplicaten.length} groep(en) met meerdere productnummers gevonden. Bij samenvoegen wordt het kortste productnummer (of BIB-* SKU) gekozen als hoofd-product, en alle orderregels worden naar dat productnummer verplaatst.
              </p>
              {duplicaten.map((g, idx) => (
                <div key={g.normalized_naam} className="border border-slate-200 rounded-md p-3 bg-slate-50/50">
                  <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500 mb-2">
                    {g.normalized_naam}
                  </p>
                  <ul className="space-y-1 text-sm">
                    {g.producten.map((p) => (
                      <li key={p.productnummer} className="flex justify-between">
                        <span className="font-mono text-xs text-slate-700">{p.productnummer}</span>
                        <span className="text-slate-500 truncate ml-3">{p.productnaam}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicaten(null)}>Sluiten</Button>
            {duplicaten?.length > 0 && (
              <Button onClick={autoMerge} className="bg-slate-900 hover:bg-slate-800" data-testid="auto-merge-btn">
                <Layers className="h-4 w-4 mr-1.5" />
                Alles samenvoegen
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
